import type { JSX, PropsWithChildren } from 'react';
import React from 'react';
import name from 'react-display-name';
import hoistNonReactStatics from 'hoist-non-react-statics';

export interface WithFirstTouchComponent<
  P extends keyof JSX.IntrinsicElements | object,
> extends React.Component<
  (P extends keyof JSX.IntrinsicElements ? Omit<JSX.IntrinsicElements[P], 'ref'> : P) &
    WithFirstTouchProps<P>
> {
  resetFirstTouch: () => void;
}
interface WithFirstTouchProps<P extends keyof JSX.IntrinsicElements | object> {
  refKey?: string;
  innerRef?: P extends keyof JSX.IntrinsicElements ? JSX.IntrinsicElements[P]['ref'] : React.Ref<P>;
  onFirstTouch: (event: Event) => void;
  onTouchStart?: (event: Event) => void;
  onClick?: (event: Event) => void;
}
export const withFirstTouch = <P extends keyof JSX.IntrinsicElements | object>(
  Component: P extends keyof JSX.IntrinsicElements
    ? keyof JSX.IntrinsicElements
    : React.ComponentType<P>
) => {
  type Props = (P extends keyof JSX.IntrinsicElements
    ? Omit<JSX.IntrinsicElements[P], 'ref'> & React.ClassAttributes<WithFirstTouchComponent<P>>
    : P) &
    WithFirstTouchProps<P>;
  class WithFirstTouch extends React.Component<PropsWithChildren<Props>> {
    static displayName = `withFirstTouch(${name(Component)})`;
    static defaultProps = {
      refKey: 'ref',
    };

    isTouchEnabled: boolean;
    isFirstTouch: boolean;

    constructor(props) {
      super(props);

      this.isFirstTouch = true;
      this.isTouchEnabled = false;

      this.onClick = this.onClick.bind(this);
      this.onTouchStart = this.onTouchStart.bind(this);
    }

    onTouchStart(event: Event) {
      this.isTouchEnabled = true;
      if (this.props.onTouchStart) {
        this.props.onTouchStart(event);
      }
    }

    onClick(event: Event) {
      if (this.isTouchEnabled === true && this.isFirstTouch === true) {
        this.props.onFirstTouch(event);
        this.isFirstTouch = false;
      } else if (this.props.onClick) {
        this.props.onClick(event);
      }
    }

    resetFirstTouch() {
      this.isFirstTouch = true;
    }

    render() {
      const { onClick, onTouchStart, onFirstTouch, innerRef, children, refKey, ...props } =
        this.props;

      return React.createElement(
        Component,
        {
          [refKey]: innerRef,
          onClick: this.onClick,
          onTouchStart: this.onTouchStart,
          ...props,
        } as Props,
        children
      );
    }
  }

  return isStringElement(Component)
    ? WithFirstTouch
    : hoistNonReactStatics(WithFirstTouch as any, Component as any);
};

function isStringElement(
  Component: keyof JSX.IntrinsicElements | React.ComponentType
): Component is keyof JSX.IntrinsicElements {
  return typeof Component === 'string';
}
